import GameFactory from "../game-factory.js";
import * as gameStateFunctions from "./game-state-functions.js";
import * as scoreUtil from "./score/score-util.js";
import * as matchStats from "./match-stats.js";
import * as constants from "./constants.js";
import * as rollCall from "./roll-call.js";

export default function Match({ matchConfig }) {
    var gameFactory = GameFactory();

    var matchState = {
        roundsData: []
    };

    function addFinishedGameState(gameState) {
        var startScore = getCurrentScore();
        var roundScore = scoreUtil.calculateRoundScore(gameState);
        var endScore = scoreUtil.combineScores(startScore, roundScore);
        // Stats must be snapshotted HERE, from the live gameState: the replay trim
        // below drops the odometers/steering segments they derive from, and the
        // victory lap that follows would otherwise pollute them.
        var roundStats = matchStats.computeRoundStats(gameState);
        var replayGameState = gameStateFunctions.extractReplayGameState(gameState);
        matchState.roundsData.push({ startScore, roundScore, endScore, roundStats, gameState: replayGameState });
    }

    function getMatchStats() {
        return matchStats.aggregateMatchStats(matchState.roundsData.map(function (roundData) {
            return roundData.roundStats;
        }));
    }

    function getCurrentScore() {
        if (matchState.roundsData.length === 0) {
            return scoreUtil.getStartScore(matchConfig.players);
        } else {
            return matchState.roundsData[matchState.roundsData.length - 1].endScore;
        }
    }

    function isMatchOver() {
        var scoreSortedPlayers = scoreUtil.createSortedList(getCurrentScore());
        if (scoreSortedPlayers.length < 2) {
            return false; // Need at least two players to have a match winner.
        }
        var lead = scoreSortedPlayers[0].score - scoreSortedPlayers[1].score;

        // Rounds mode: play a fixed number of rounds, then end as soon as there's
        // a clear leader. A tie at the top keeps the match going (extra rounds)
        // until someone is ahead, no matter the round count.
        if (matchConfig.maxRounds) {
            if (matchState.roundsData.length < matchConfig.maxRounds) {
                return false;
            }
            return lead >= 1;
        }

        // Score mode (default): first to maxScore wins outright. The only thing
        // that keeps the match going is an exact tie at the top (equal scores):
        // then extra rounds are played until someone is ahead. A one-point lead
        // (e.g. 47 vs 46) is already a winner, not a tie.
        if (lead < 1) {
            return false; // Exact tie at the top: play another round.
        }
        return scoreSortedPlayers[0].score >= matchConfig.maxScore;
    }

    function prepareNextGame(seed) {
        // Every round's countdown is preceded by the roll call (roll-call.js); the
        // first round can still be sized differently via START_PHASE_DURATION_FIRST.
        var startPhaseDuration = matchState.roundsData.length === 0
            ? constants.START_PHASE_DURATION_FIRST
            : constants.START_PHASE_DURATION;
        // A ranked match opens with the match-found banner over the arena, so its
        // first countdown is longer (the numbers start once the banner has left).
        if (matchState.roundsData.length === 0 && typeof matchConfig.startPhaseDurationFirst === "number") { startPhaseDuration = matchConfig.startPhaseDurationFirst; }
        // A dev harness can shorten the wait (the bot trait lab): its own
        // countdown length, and no roll call. Real matches never set these.
        if (typeof matchConfig.startPhaseDuration === "number") { startPhaseDuration = matchConfig.startPhaseDuration; }
        var skipRollCall = matchConfig.skipRollCall === true;
        return gameFactory.create({
            seed: seed,
            map: matchConfig.map,
            players: matchConfig.players,
            startPhaseDuration: startPhaseDuration,
            // Tied leaders share the first beat (see roll-call.js rollCallBeats), so
            // the roll call is sized by beats, not heads; the client schedules from
            // the same rule.
            rollCallDuration: skipRollCall ? 0 : rollCall.rollCallDuration(rollCall.rollCallBeats(matchConfig.players.map(function (p) { return p.id; }), getCurrentScore())),
            // Optional per-match power-up / worm tuning (local play's settings panel).
            // Undefined for online + classic local play, so defaults apply.
            disabledEffectTypes: matchConfig.disabledEffectTypes,
            disabledPowerUps: matchConfig.disabledPowerUps,
            powerUpSpawnChance: matchConfig.powerUpSpawnChance,
            wormSpeed: matchConfig.wormSpeed,
            // The match's crowd level, when the host decided one (ai/crowd.js
            // resolveCrowdLevel); undefined = the roster decides.
            crowdLevel: matchConfig.crowdLevel,
            // The host's retuned power-up values for bots' greed, when it has any
            // (ai/powerup-values.js); undefined = the defaults.
            powerUpGreedValues: matchConfig.powerUpGreedValues,
            // A Respawn match (core/respawn.js): { roundTime } or true; Classic leaves it unset
            respawnRound: matchConfig.respawn
        });
    }

    return {
        addFinishedGameState,
        getCurrentScore,
        getMatchStats,
        prepareNextGame,
        isMatchOver,
        matchConfig,
        matchState
    }
};
